import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { Product } from '~/lib/data-contract/salesforce';
import { SalesforceProductSpecialPrice } from '~/lib/salesforce/types';
import { breakpoints } from '~/theme/breakpoints';
import { SectionTitle } from './SectionTitle/SectionTitle';
import { ImageWrap, SmallProductCard } from './SmallProductCard';
import { DigitalProductCard } from './DigitalProductCard';
import { getSummeryImage } from '~/utils/image-group-utils';
import { theme } from '~/theme';
import Spacer from '~/theme/design-system/components/Spacer';

const ListWrapper = styled.div<{
    hasBorder?: boolean;
    large?: boolean;
}>(({ theme, hasBorder }) => ({
    border: hasBorder ? `1px solid ${theme.colors.black}` : 'none',
    borderRadius: hasBorder ? theme.sizes.cornerRadius : 0,
    display: 'block',
    '& li:first-of-type': {
        [`${ImageWrap}`]: {
            borderTopLeftRadius: hasBorder ? theme.sizes.cornerRadius : 0,
        },
    },
    '& li:last-of-type': {
        [`${ImageWrap}`]: {
            borderBottomLeftRadius: hasBorder ? theme.sizes.cornerRadius : 0,
        },
    },
    'li:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.colors.grey35}`,
    },

    [`${SectionTitle}`]: {
        borderTop: 'none',
    },

    [breakpoints.sm]: {
        border: hasBorder ? `1px solid ${theme.colors.grey35}` : 'none',
        borderRadius: 0,
    },
}));

export const SummaryList = styled(
    ({ className, title, products, large, showMemberPrice }: Omit<Product, 'id'>) => {
        const formatProduct = useCallback(
            (product: Product) => {
                return {
                    ...product,
                    basePrice: product.basePrice ?? product.price,
                    description: product.c_synopsis,
                    image: getSummeryImage(product),
                    memberprice: product.c_memberprice,
                    unitPrice: product.c_unitPrice,
                    group: product.c_group,
                    /**
                     * TODO(IMC-1671): Drop this once the data-contract `Product` is retired.
                     * It declares the special price money fields as strings; Salesforce sends
                     * numbers, which is what `SalesforceProductSpecialPrice` describes.
                     */
                    c_specialPrices: product.c_specialPrices as
                        | SalesforceProductSpecialPrice
                        | undefined,
                };
            },
            [JSON.stringify(products)],
        );

        return (
            <ListWrapper className={className}>
                {title && <SectionTitle>{title}</SectionTitle>}
                <Spacer space={theme.space[4]} />
                <ul>
                    {Array.isArray(products) &&
                        products.map(formatProduct).map((product) => {
                            return (
                                <li key={product.id}>
                                    {product.group === 'electronicgiftcard' ? (
                                        <DigitalProductCard {...product} large={large} />
                                    ) : (
                                        <SmallProductCard
                                            {...product}
                                            large={large}
                                            showMemberPrice={showMemberPrice}
                                        />
                                    )}
                                </li>
                            );
                        })}
                </ul>
            </ListWrapper>
        );
    },
)({});
